import { useState } from 'react';
import { Store, Users, MapPin, Bell, Save } from 'lucide-react';

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('store');

  const tabs = [
    { id: 'store', label: 'Store', icon: Store },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'pickup', label: 'Pickup Location', icon: MapPin },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold mb-6">Settings</h2>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-48 space-y-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-colors ${
                activeTab === tab.id
                  ? 'bg-uber-green text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="card">
            {activeTab === 'store' && <StoreSettings />}
            {activeTab === 'team' && <TeamSettings />}
            {activeTab === 'pickup' && <PickupSettings />}
            {activeTab === 'notifications' && <NotificationSettings />}
          </div>
        </div>
      </div>
    </div>
  );
}

function StoreSettings() {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Store Information</h3>
      <form className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Store Name
          </label>
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
            placeholder="Your Store Name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Shopify Domain
          </label>
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
            placeholder="your-store.myshopify.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact Email
          </label>
          <input
            type="email"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
            placeholder="contact@yourstore.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact Phone
          </label>
          <input
            type="tel"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
            placeholder="+1 (555) 123-4567"
          />
        </div>
        <button type="submit" className="btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" />
          Save Changes
        </button>
      </form>
    </div>
  );
}

function TeamSettings() {
  const [members] = useState([
    { id: '1', name: 'John Smith', phone: '+1 (555) 123-4567', role: 'Production' },
    { id: '2', name: 'Jane Doe', phone: '+1 (555) 987-6543', role: 'Manager' },
  ]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Team Members</h3>
        <button className="btn-primary text-sm">Add Member</button>
      </div>
      <p className="text-gray-600 mb-4">
        Team members will receive notifications when orders are ready for pickup.
      </p>
      <div className="space-y-3">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
          >
            <div>
              <p className="font-medium">{member.name}</p>
              <p className="text-sm text-gray-600">{member.phone}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{member.role}</span>
              <button className="text-red-600 hover:text-red-700 text-sm">
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PickupSettings() {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Pickup Location</h3>
      <p className="text-gray-600 mb-4">
        This is where drivers will pick up packages for delivery.
      </p>
      <form className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address
          </label>
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
            placeholder="123 Main Street"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              City
            </label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
              placeholder="City"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State
            </label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
              placeholder="CA"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code
            </label>
            <input
              type="text"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
              placeholder="90210"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pickup Phone
            </label>
            <input
              type="tel"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pickup Instructions for Driver
          </label>
          <textarea
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
            placeholder="Enter through the back door, ask for the production team..."
          />
        </div>
        <button type="submit" className="btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" />
          Save Changes
        </button>
      </form>
    </div>
  );
}

function NotificationSettings() {
  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Notification Preferences</h3>
      <div className="space-y-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            defaultChecked
            className="w-5 h-5 rounded border-gray-300 text-uber-green focus:ring-uber-green"
          />
          <div>
            <p className="font-medium">New Order Alerts</p>
            <p className="text-sm text-gray-600">
              Get notified when a new order comes in
            </p>
          </div>
        </label>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            defaultChecked
            className="w-5 h-5 rounded border-gray-300 text-uber-green focus:ring-uber-green"
          />
          <div>
            <p className="font-medium">Driver Approaching</p>
            <p className="text-sm text-gray-600">
              Get notified when driver is en route to pickup
            </p>
          </div>
        </label>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            defaultChecked
            className="w-5 h-5 rounded border-gray-300 text-uber-green focus:ring-uber-green"
          />
          <div>
            <p className="font-medium">Delivery Complete</p>
            <p className="text-sm text-gray-600">
              Get notified when a delivery is completed
            </p>
          </div>
        </label>
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            defaultChecked
            className="w-5 h-5 rounded border-gray-300 text-uber-green focus:ring-uber-green"
          />
          <div>
            <p className="font-medium">Escalation Alerts</p>
            <p className="text-sm text-gray-600">
              Get called if a delivery issue needs attention
            </p>
          </div>
        </label>
        <button className="btn-primary flex items-center gap-2">
          <Save className="w-4 h-4" />
          Save Preferences
        </button>
      </div>
    </div>
  );
}
